import React, { useEffect, useRef, useState } from 'react';

import { locationForRoute } from 'files/routes.js';
import { CHANGED, dismiss, isComplete, isDismissed, isInProgress, load } from 'files/quick-look-storage.js';
import { publishedArticles, useArticles } from 'files/articles.js';
import { scoreAnswers } from 'files/quick-look-model.js';
import { isMobile as phoneWidth } from 'files/plain.js';

/* Nav and footer.

   The link set is declared once, in LINKS below, and rendered by one
   component that both the header and the footer use. It was previously written
   out twice inside this file and the two copies had already drifted — the
   article pages were serving a nav with three of the five items missing.

   Section links work from anywhere. On the home page they scroll; on any other
   page they route home carrying the section, and HomePage scrolls to it once it
   has rendered. */

/* One list, two kinds of destination: `view` routes to a page, `section` scrolls
   to a block on the home page. How We Work is a page of its own now — the home
   page keeps a summary of it and links across. */
const LINKS = [
    { label: 'Common Problems', section: 'problems' },
    { label: 'How We Work', view: 'how-we-work' },
    { label: 'Why Dura Labs', section: 'why' },
    { label: 'Pricing', view: 'ways-to-work' },
    { label: 'Articles', view: 'articles' },
    { label: 'Contact', view: 'contact' }
];

/* The footer is not the nav with a rule above it. It is where someone lands
   when they have read to the bottom and are deciding what to do, so it is
   grouped by what they might be looking for rather than mirroring the header.

   Sections are declared once, here, for the same reason LINKS is: the previous
   footer duplicated its links inline and the two copies had already drifted. */
const FOOTER = [{
    title: 'What we do',
    links: [
        { label: 'Common problems we solve', section: 'problems' },
        { label: 'How we work', view: 'how-we-work' },
        { label: 'Ways to work with us', view: 'ways-to-work' },
        { label: 'Why Dura Labs', section: 'why' }
    ]
},{
    /* The reading column fills itself from whatever is published — see
       readingLinks below. Only the way into the library is fixed. */
    title: 'Worth reading',
    links: [{ label: 'All articles', view: 'articles' }]
},{
    title: 'Start somewhere',
    links: [
        { label: 'Take a Quick Look', view: 'quick-look' },
        { label: 'Contact us', view: 'contact' }
    ]
}];

/* Scrolling is handled here rather than left to the browser. The page is client
   rendered, so on a cold load with a fragment in the URL the browser looks for
   #problems before React has produced it, finds nothing, and gives up — the link
   silently does nothing. */
export const scrollToSection = id => {

    const target = document.getElementById(id);
    if(!target) {
        return false;
    }

    /* window.scrollTo rather than target.scrollIntoView: scrollIntoView picks
       whichever scroll container it finds first, and addressing the window
       directly takes the ancestors out of the question entirely.

       The sticky nav is measured rather than assumed, so the heading lands below
       it instead of underneath it at whatever height the nav happens to be. */
    const nav = document.querySelector('.dl-nav');

    /* Twenty pixels of breathing room under the nav, except when the target is
       a whole section.
 
       The sections on the home page each carry their own background, so landing
       twenty pixels above one leaves a twenty pixel band of the previous
       section's colour under the navigation. It reads as a stripe nobody drew.
       A section brings its own generous padding anyway, so the heading is not
       tight against the bar without it.
 
       Headings inside an article are the other case and do want the gap: they
       are inline in a column of prose with nothing above them to sit against. */
    const banded = target.tagName === 'SECTION';
    const clearance = (nav ? nav.offsetHeight : 0) + (banded ? 0 : 20);
    const top = target.getBoundingClientRect().top + window.pageYOffset - clearance;

    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ behavior: still ? 'auto' : 'smooth', top: Math.max(0, top) });

    // replace rather than push: the section is where you are, not somewhere you
    // navigated to, and a history entry per nav click makes back unusable
    window.history.replaceState({}, '', `#${id}`);
    return true;
}

const SiteLinks = ({ button, utils, variant }) => {

    const go = (view, props) => evt => {
        evt.preventDefault();
        utils.navigation.set({ view: view, ...props });
    }

    const onSection = id => evt => {

        evt.preventDefault();
        if(variant === 'home') {
            scrollToSection(id);
            return;
        }
        // HomePage reads active.section on arrival and scrolls to it
        utils.navigation.set({ view: 'home', section: id });
    }

    return (
        <>
            {LINKS.map(link => (link.view ? (
                <a href={locationForRoute({ view: link.view })} key={link.label} onClick={go(link.view)}>
                    {link.label}
                </a>
            ) : (
                <a href={`/#${link.section}`} key={link.label} onClick={onSection(link.section)}>
                    {link.label}
                </a>
            )))}
            {/* "Take a Quick Look" here and on every button that opens the tool,
                sitewide. The nav used to carry the name alone, on the rule that
                the name was a destination rather than an instruction; the
                instruction form says what pressing it does.

                Suppressed on a page that is selling something else. The Deep
                Look page asks for $199 and this offers a different product for
                free, a few hundred pixels apart, which is two offers competing
                in one glance. The free tool is still reachable from the footer
                and from every other page on the site; what it does not get is a
                button beside the one we want pressed. */}
            {variant === 'offer' ? (
                /* The same slot, spent on the page's own ask. Dropping the
                   button altogether left a reader who had scrolled past the hero
                   with nothing to press until the price card near the bottom.
                   It scrolls there rather than starting checkout, because a nav
                   item that charges a card on one tap is not what anybody
                   expects a nav item to do. */
                <a
                className={button ? 'dl-btn dl-btn-outline' : undefined}
                href={'#get'}
                onClick={evt => { evt.preventDefault(); scrollToSection('get'); }}>
                    Start a Deep Look
                </a>
            ) : (
                <a
                className={button ? 'dl-btn dl-btn-outline' : undefined}
                href={locationForRoute({ view: 'quick-look' })}
                onClick={go('quick-look')}>
                    Take a Quick Look
                </a>
            )}
        </>
    )
}

/* A Quick Look held in this browser, surfaced as a strip under the nav.

   It lives in the chrome rather than on the home page because a return visit
   rarely lands on the home page — it lands on the article that brought them
   back. Half-finished answers the owner cannot see a way back to are the same
   as no answers at all.

   Only ever a strip: it says what is held and offers the way back in. Anything
   more turns the top of every page into a pitch. */

const QuickLookResume = ({ utils }) => {

    const isMobile = phoneWidth();
    // read after mount, and again whenever the record changes, because this is
    // mounted for the whole visit while the tool runs in a modal over it
    const [record, setRecord] = useState(null);
    const [hidden, setHidden] = useState(isDismissed);

    useEffect(() => {
        /* Both, on every change. The dismissal is cleared by save(), so a
           returning owner who reopens the Quick Look and answers something gets
           the strip back without this component knowing anything about why —
           it just re-reads the flag alongside the record. */
        const sync = () => {
            setRecord(load());
            setHidden(isDismissed());
        };
        sync();
        window.addEventListener(CHANGED, sync);
        return () => window.removeEventListener(CHANGED, sync);
    }, []);

    if(!record || hidden) {
        return null;
    }

    const finished = isComplete(record);
    if(!finished && !isInProgress(record)) {
        return null;
    }

    const onOpen = evt => {
        evt.preventDefault();
        utils.navigation.set({ view: 'quick-look' });
    }

    // the flag lives with the record it guards; see quick-look-storage
    const onDismiss = () => dismiss();

    /* Named from the read the owner actually saw when there is one, and from
       the authored read otherwise — never from a stored copy of the sentence,
       so a change to either can't leave a stale claim sitting in the nav. */
    const named = () => {
        if(record.read && record.read.areas && record.read.areas.length > 0) {
            return record.read.areas[0].title.toLowerCase().replace(/\.$/, '');
        }
        const result = scoreAnswers(record.answers || {});
        return result.findings.length > 0 ? result.findings[0].name.toLowerCase() : null;
    }

    const opening = finished ? named() : null;

    return (
        <div className={'dl-resume'}>
            {/* No .dl-wrap. Its max-width and width: 100% are what made this
                span the page; a card is measured by what is in it. */}
            <div className={'dl-resume-inner'}>
                <p style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {finished ? (
                        <>
                        <b>Your Quick Look is saved on this device.</b>{isMobile ? <br /> : null}
                        </>
                    ) : (
                        <b>You left your Quick Look part-way through.</b>
                    )}
                </p>
                {finished && (
                    <p>{opening ? ` The clearest opening in it: ${opening}.` : ''}</p>
                )}
                <a className={'dl-quiet'} href={locationForRoute({ view: 'quick-look' })} onClick={onOpen}>
                    {finished ? 'Read it again' : 'Pick up where you left off'} →
                </a>

                {/* Taken out of the flex flow on purpose.

                    In the flow it had to be positioned by the same rule that
                    positions the sentence and the link, which is why it ended up
                    trailing the link at the bottom of the card and marooned at
                    the far edge of a wide bar. Absolute, it is free of both: the
                    sentence and the link can be a centred pair on the bar and a
                    stacked pair on the card, and the dismiss sits where a
                    dismiss belongs in each case. */}
                <button
                aria-label={'Hide this until my next visit'}
                className={'dl-resume-close'}
                onClick={onDismiss}
                title={'Hide this until my next visit'}
                type={'button'}>
                    {/* Drawn rather than typed. A multiplication sign renders at
                        whatever weight and baseline the font decides, and sits
                        noticeably high in Inter. */}
                    <svg aria-hidden={'true'} fill={'none'} height={'11'} viewBox={'0 0 11 11'} width={'11'}>
                        <path d={'M1 1 L10 10 M10 1 L1 10'} stroke={'currentColor'} strokeLinecap={'round'} strokeWidth={'1.5'} />
                    </svg>
                </button>
            </div>
        </div>
    )
}

/* The header's real height, published to css as --dl-chrome.

   Anything pinned below the header has to know where it ends, and that height
   is not a constant. The Quick Look strip appears and disappears above the nav
   during a single visit, and the links wrap on a narrow screen. A number typed
   into a stylesheet is correct until the first time it isn't, and the way it
   fails is silent: the contents rail simply sits underneath the nav and looks
   like a styling mistake rather than a stale measurement.

   scrollToSection measures the same element for the same reason. This is that
   measurement made continuous and handed to the stylesheet, so nothing else has
   to reach for the dom to place itself. */
const useChromeHeight = () => {

    const header = useRef(null);

    useEffect(() => {

        const node = header.current;
        if(!node) {
            return;
        }

        const publish = () => document.documentElement.style.setProperty(
            '--dl-chrome', `${Math.round(node.getBoundingClientRect().height)}px`
        );
        publish();

        // observed rather than measured once: the strip mounts and unmounts
        // under this element without the header itself re-rendering
        if(typeof ResizeObserver === 'undefined') {
            return;
        }
        const observer = new ResizeObserver(publish);
        observer.observe(node);
        return () => observer.disconnect();

    }, []);

    return header;
}

/* Whether the reader has left the top of the page behind.

   Only the nav uses it, and only to decide whether the bar has to carry a way
   into the Quick Look. See the note on .dl-nav-cta in home.css for why that is
   a scroll position rather than something always in the bar.

   A listener rather than an IntersectionObserver on a sentinel: the sentinel
   would have to live in the page flow underneath a sticky header, which means
   every page owning an element for the benefit of the chrome. One number read
   off the window belongs to the chrome alone.

   Passive, and coalesced to a frame. Scroll fires far faster than it paints,
   and a setState per event is the classic way to make a page feel heavy while
   scrolling — which is the opposite of what this is for. */
const useScrolledPastHero = () => {

    const [past, setPast] = useState(false);

    useEffect(() => {

        let frame = null;

        const read = () => {
            frame = null;
            // one screen. Far enough that the hero and its button are gone
            // rather than half-visible, which is when a second copy of the same
            // invitation reads as nagging.
            setPast(window.scrollY > window.innerHeight);
        };

        const onScroll = () => {
            if(frame === null) {
                frame = window.requestAnimationFrame(read);
            }
        };

        read();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            if(frame !== null) {
                window.cancelAnimationFrame(frame);
            }
        };

    }, []);

    return past;
}

export const SiteNav = ({ utils, variant }) => {

    const home = variant === 'home';
    const header = useChromeHeight();
    const scrolled = useScrolledPastHero();

    /* The menu, below about eight hundred and sixty pixels.
 
       Every link but the Quick Look button has been hidden at that width for as
       long as the breakpoint has existed, and nothing replaced them, so a phone
       has had one destination and no way to reach any other. A stylesheet rule
       named .dl-nav-toggle has been sitting unused this whole time, waiting for
       the button it was written for. */
    const [open, setOpen] = useState(false);

    const onHome = evt => {
        evt.preventDefault();
        setOpen(false);
        utils.navigation.set({ view: 'home' });
    }

    const onQuickLook = evt => {
        evt.preventDefault();
        setOpen(false);
        utils.navigation.set({ view: 'quick-look' });
    }

    /* Closed by arriving somewhere. A panel that stays open over the page it
       just navigated to is the most common way this is got wrong. */
    const onNavigate = () => setOpen(false);

    // Escape closes it, the way it closes the Quick Look
    useEffect(() => {

        if(!open) {
            return;
        }
        const onKeyDown = evt => evt.key === 'Escape' && setOpen(false);
        window.addEventListener('keydown', onKeyDown);

        /* The page behind must not scroll under an open panel. Without this a
           thumb dragging over the menu moves the article underneath it. */
        document.body.style.overflow = 'hidden';

        return () => {
            window.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = '';
        };

    }, [open]);

    // a resize back up to a width that shows the full bar leaves the panel open
    // over a nav that no longer needs it
    useEffect(() => {

        if(!open) {
            return;
        }
        const wide = window.matchMedia('(min-width: 861px)');
        const onChange = event => event.matches && setOpen(false);
        wide.addEventListener('change', onChange);
        return () => wide.removeEventListener('change', onChange);

    }, [open]);

    return (
        <>

        <header className={`dl-nav ${open ? 'is-open' : ''} ${scrolled ? 'is-scrolled' : ''}`}>

            {/* The bar, and only the bar, is what --dl-chrome measures.
 
                The ref used to sit on the header itself, which was correct until
                the menu was added inside it. Then opening the menu grew the
                observed element, which published a new height, which changed the
                menu's own max-height, which grew the element again: a
                ResizeObserver loop, reported by the browser and doing real work
                on every frame.
 
                Observing the bar alone also happens to be the right definition.
                Everything that reads --dl-chrome is positioning itself below the
                sticky chrome, and an overlay panel is not chrome. */}

            <div ref={header}>
                <div className={'dl-wrap dl-nav-inner'}>
                    <a className={'dl-mark'} href={home ? '#top' : '/'} onClick={home ? undefined : onHome}>
                        <img alt={''} src={'/images/dr-logo-circle.png'} style={{ height: 25, width: 25 }} />
                        Dura Labs
                    </a>
                    <nav className={'dl-nav-links'}>
                        <SiteLinks button={true} utils={utils} variant={variant} />
                    </nav>

                    {/* The way into the Quick Look on a phone, once the hero's
                        own button is gone. The full nav carries this as a link
                        in .dl-nav-links; below 860 that list is hidden and this
                        takes over, so there is never a moment with two of them
                        in the bar at once. Rendered on every width and hidden by
                        the stylesheet, the same way .dl-menu is. */}
                    <a
                    className={'dl-nav-cta'}
                    href={locationForRoute({ view: 'quick-look' })}
                    onClick={onQuickLook}
                    tabIndex={scrolled ? undefined : -1}>
                        Quick Look
                    </a>

                    {/* Two bars rather than three. The third is decoration on a mark
                        that is already universally understood, and two closing into
                        a cross is a shorter distance to travel. */}
                    <button
                    aria-controls={'dl-menu'}
                    aria-expanded={open}
                    aria-label={open ? 'Close menu' : 'Open menu'}
                    className={'dl-nav-toggle'}
                    onClick={() => setOpen(!open)}
                    type={'button'}>
                        <span />
                        <span />
                    </button>
                </div>
            </div>

            {/* Rendered always and hidden by the stylesheet, rather than mounted
                on open. It has to be in the document for the panel to animate
                and for a screen reader to find what the button controls. */}
            <div className={'dl-menu'} id={'dl-menu'} hidden={!open}>
                <nav className={'dl-menu-links'} onClick={onNavigate}>
                    <SiteLinks button={true} utils={utils} variant={variant} />
                </nav>
            </div>
        </header>

        {/* Outside the header, and it has to be.

            .dl-nav carries a backdrop-filter above 860px, and a filtered element
            is the containing block for any fixed-position descendant. Nested
            inside it, the card's `bottom` was measured from the foot of the
            navigation bar rather than the foot of the window, so it sat under
            the logo instead of at the bottom of the screen. On a phone it
            worked, purely because the filter is dropped at that width.

            After the header rather than before it, so the rule that hides it
            behind an open menu can reach it as a sibling. */}
        <QuickLookResume utils={utils} />

        </>
    )
}

/* The newest few pieces, as footer links.

   These were three slugs typed into the array above, which meant the footer
   quietly advertised whatever was current the day somebody last remembered to
   edit it. Four pieces have been published since. A footer that names old work
   as the work worth reading is worse than one that names none, because it is
   the part of the page that looks maintained.

   The label comes from `short` rather than the title. That field was written to
   be a compact description of a piece for exactly this kind of inline link, and
   titles run to sixty characters in a column two hundred wide. The "Read: "
   prefix belongs to the sentence it was written for and not to a list, so it
   comes off, and the first letter is raised because several of them were
   written to sit mid-sentence in lower case. */
const readingLinks = articles => publishedArticles(articles).slice(0, 3).map(article => {
    const label = String(article.short || article.title).replace(/^Read:\s*/i, '');
    return {
        label: label.charAt(0).toUpperCase() + label.slice(1),
        slug: article.slug,
        view: 'article'
    };
});

export const SiteFooter = ({ utils, variant }) => {

    const home = variant === 'home';
    const library = useArticles();

    const onHome = evt => {
        evt.preventDefault();
        utils.navigation.set({ view: 'home' });
    }

    const go = (view, props) => evt => {
        evt.preventDefault();
        utils.navigation.set({ view: view, ...props });
    }

    const onSection = id => evt => {
        evt.preventDefault();
        if(variant === 'home') {
            scrollToSection(id);
            return;
        }
        utils.navigation.set({ view: 'home', section: id });
    }

    const href = link => (link.view
        ? locationForRoute({ slug: link.slug, view: link.view })
        : `/#${link.section}`);

    return (
        <footer className={'dl-footer'}>
            <div className={'dl-wrap'}>

                {/* The mark and the sentence sit above the columns rather than
                    beside them. Someone who has read this far has read enough to
                    be deciding, and one line saying what we do costs less than
                    making them scroll back up to remember. */}
                <div className={'dl-footer-top'}>
                    <div className={'dl-footer-mark'}>
                        <a className={'dl-mark'} href={home ? '#top' : '/'} onClick={home ? undefined : onHome}>
                            <img alt={''} src={'/images/dr-logo-circle.png'} style={{ height: 25, width: 25 }} />
                            Dura Labs
                        </a>
                        {/* The same widening the hero got, in fewer words.

                            "Businesses that have outgrown the tools they started
                            on" is a good description of half the people we want
                            and a closed door on the other half: it takes for
                            granted that there are tools, and that the problem is
                            that they no longer fit. Somebody building a first
                            product has nothing to have outgrown. The two halves
                            are named as equals here, the way they are in the
                            hero, and a footer is the place to say it shortest. */}
                        <p>
                            Custom software for fixing what you have and building what you
                            don’t. It works alongside the systems you already run.
                        </p>
                    </div>
                </div>

                <nav className={'dl-footer-cols'}>
                    {FOOTER.map(column => (
                        <div className={'dl-footer-col'} key={column.title}>
                            <span className={'dl-footer-title'}>{column.title}</span>
                            {[
                                ...column.links,
                                ...(column.title === 'Worth reading' ? readingLinks(library.articles) : [])
                            ].map(link => (
                                <a
                                href={href(link)}
                                key={link.label}
                                onClick={link.view ? go(link.view, link.slug ? { slug: link.slug } : {}) : onSection(link.section)}>
                                    {link.label}
                                </a>
                            ))}
                        </div>
                    ))}
                </nav>

                <div className={'dl-footer-foot'}>
                    {/* Read at render rather than written down. It said 2021 for
                        five years, which on a site whose argument is care and
                        maintenance reads as a page nobody has been back to. */}
                    <span className={'dl-meta'}>© {new Date().getFullYear()} Dura Labs LLC</span>
                    {/* the documents someone looks for at the foot of a page, opposite the name they belong to */}
                    <span className={'dl-meta dl-footer-legal'}>
                        <a href={locationForRoute({ view: 'privacy' })} onClick={go('privacy')}>Privacy policy</a>
                        <a href={locationForRoute({ view: 'terms' })} onClick={go('terms')}>Terms of service</a>
                    </span>
                </div>
            </div>
        </footer>
    )
}
